import { radiusCva, useProvider } from "@overmap-ai/blocks"
import * as turf from "@turf/turf"
import { cx } from "class-variance-authority"
import type { Geometry } from "geojson"
import { memo, type PropsWithChildren, useCallback } from "react"
import { twMerge } from "tailwind-merge"

import { useAppSelector } from "../../store"
import { selectMapStyle } from "../../store/selectors"
import { useViewGeometryOnMap } from "../hooks"
import { Map } from "./Map"

interface MinimapProps extends PropsWithChildren {
	geometry: Geometry
	onMapClick?: () => void
}

export const Minimap = memo<MinimapProps>((props) => {
	const { children, geometry, onMapClick } = props
	const { radius } = useProvider()
	const mapStyle = useAppSelector(selectMapStyle)
	const viewGeometryOnMap = useViewGeometryOnMap()

	const handleViewLocationOnMapClick = useCallback(() => {
		onMapClick?.()
		viewGeometryOnMap(geometry)
	}, [geometry, onMapClick, viewGeometryOnMap])

	return (
		<div
			className={twMerge(
				cx(
					"relative mb-1 h-1/2 w-full overflow-hidden object-cover pt-[200px]",
					radiusCva({ radius, maxLarge: true }),
				),
			)}
			onClick={handleViewLocationOnMapClick}
		>
			<div className="pointer-events-none absolute inset-0">
				<Map
					mapStyle={mapStyle}
					initialViewState={{
						// @ts-expect-error REASON: bbox provides extra coords for 3d geojson
						bounds: turf.bbox(geometry),
						fitBoundsOptions: {
							padding: 40,
						},
					}}
				>
					{children}
				</Map>
			</div>
		</div>
	)
})
Minimap.displayName = "Minimap"
